import { useCallback } from 'react';
import { apiClient } from '@/services';
import { getChatNameValidationMessage, getSanitizedChatName } from '@/utils/chatValidation';
import { useAuth } from './useAuth';
import type { Chat } from '@/services/api';

/**
 * Hook for managing chat list operations
 */
export function useChats() {
  const { getAccessToken } = useAuth();

  const getChats = useCallback(async (pageNumber?: number, pageSize?: number): Promise<Chat[]> => {
    // Get access token
    let currentToken: string | undefined = undefined;
    try {
      currentToken = await getAccessToken();
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Authentication failed');
    }

    try {
      const chats = await apiClient.getChats(currentToken, pageNumber, pageSize);
      return chats;
    } catch (err) {
      console.error('❌ apiClient.getChats failed:', err);
      // Preserve the original error to maintain type information (ApiStatusError, NetworkError, etc.)
      throw err;
    }
  }, [getAccessToken]);

  const renameChat = useCallback(async (chatId: number, name: string): Promise<void> => {
    const trimmedName = getSanitizedChatName(name);
    const validationMessage = getChatNameValidationMessage(name);
    if (validationMessage !== null) {
      throw new Error(validationMessage);
    }

    let currentToken: string | undefined;
    try {
      currentToken = await getAccessToken();
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Authentication failed');
    }

    try {
      await apiClient.renameChat(chatId, trimmedName, currentToken);
    } catch (err) {
      console.error(`❌ apiClient.renameChat(${chatId}) failed:`, err);
      throw err;
    }
  }, [getAccessToken]);

    /**
   * Delete a chat
   */
  const deleteChat = useCallback(async (chatId: number): Promise<void> => {
    let currentToken: string | undefined;
    try {
      currentToken = await getAccessToken();
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Authentication failed');
    }

    try {
      await apiClient.deleteChat(chatId, currentToken);
    } catch (err) {
      console.error(`❌ apiClient.deleteChat(${chatId}) failed:`, err);
      throw err; // re-throw so the component can handle state updates
    }
  }, [getAccessToken]);

  const pinChat = useCallback(async (chatId: number, isPinned: boolean): Promise<Chat> => {
    let currentToken: string | undefined;
    try {
      currentToken = await getAccessToken();
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Authentication failed');
    }

    try {
      return await apiClient.pinChat(chatId, isPinned, currentToken);
    } catch (err) {
      console.error(`❌ apiClient.pinChat(${chatId}) failed:`, err);
      throw err;
    }
  }, [getAccessToken]);

  return {
    getChats,
    renameChat,
    deleteChat,
    pinChat
  };
}
